import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import OverlayDialog from "../OverlayDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../../lib/authTokenStore.js";
import { hasSensitivePermission, invalidateMyPermissions, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { estimateGrantImpact, groupKeyId } from "../../lib/accessClient.js";
import { BUTTON, BUTTON_STRONG, BUTTON_QUIET, INPUT, PANEL, labelFor, shortLabelFor, groupName, recipientName, compactScope, recipientKey, CandidatePicker, keyOnly } from "./ActivityConfigurationFields.jsx";
import {
    ACTIVITY_CAPABILITIES, RAW_PROMPT_CAPABILITY, activityConfigurationError, activityConfigurationMutation,
    listActivityGrants, uncertainActivityMutation,
} from "../../lib/activityConfiguration.js";


export default function ActivityAccessTab() {
    const identity = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    return <ActivityGrants key={identity} />;
}

function activityEnforcement(caps) {
    if (caps?.loading) return "loading";
    if (caps?.degraded || caps?.subjectScopeDegraded) return "unknown";
    switch (caps?.subjectScope?.disposition) {
        case "legacy_passthrough": return "off";
        case "unrestricted":
        case "scoped":
        case "empty":
        case "viewer_unmapped":
        case "no_active_generation": return "on";
        default: return "unknown";
    }
}

function EnforcementPill({ enforcement }) {
    return <span role="status" aria-busy={enforcement === "loading"}
        className="rounded-full bg-[color:var(--control-bg)] px-2 py-[2px] text-[10px] uppercase tracking-wide text-[color:var(--muted)]">
        {enforcement === "off" ? "Enforcement off"
            : enforcement === "on" ? "Enforcement on"
                : enforcement === "loading" ? "Checking enforcement" : "Enforcement unknown"}
    </span>;
}

/// Shared by the open scope step and the collapsed scope summary, so a single
/// estimate serves both. Returns null while inactive, which also discards a
/// preview that is still in flight when the scope stops being group-based.
function useScopeImpact({ recipient, recipientKind, groups, active }) {
    const [result, setResult] = useState(null);
    const definition = JSON.stringify({
        grantee: recipientKind === "viewer" ? { type: "viewer", viewerUserId: recipient?.viewerUserId }
            : { type: "group", directoryGroup: recipient ? keyOnly(recipient) : null },
        scopeGroups: groups.map(keyOnly),
    });
    useEffect(() => {
        if (!active) return undefined;
        let current = true;
        const timer = setTimeout(() => {
            estimateGrantImpact(JSON.parse(definition)).then(value => {
                if (current) setResult({ ...value, definition });
            }).catch(() => { if (current) setResult({ definition, degraded: true }); });
        }, 180);
        return () => { current = false; clearTimeout(timer); };
    }, [definition, active]);
    return active && result?.definition === definition ? result : null;
}

const STEP_LABELS = { capability: "See", scope: "Whose", expiry: "Expires" };

function GrantStep({ name, position, answered, value, open, onOpen, children }) {
    return <section data-step={name} {...(name === "scope" ? { "data-grant-scope-picker": "" } : {})}
        className="grid gap-3 border-t border-[color:var(--line)] pt-3 first:border-t-0 first:pt-0">
        <div className="flex items-center gap-3">
            <span aria-hidden="true" className={`grid size-5 shrink-0 place-items-center rounded-full text-[10px] ${answered && !open ? "bg-[color:var(--green)] text-[color:var(--control-bg)]" : "border border-[color:var(--line)] text-[color:var(--muted)]"}`}>{position}</span>
            <span className="w-14 shrink-0 text-[color:var(--muted)]">{STEP_LABELS[name]}</span>
            {open ? null : <>
                <span className={`min-w-0 flex-1 truncate ${answered ? "font-medium" : "text-[color:var(--muted)]"}`}>{value}</span>
                <button type="button" data-step-edit={name} className={BUTTON} onClick={onOpen} aria-label={`Change ${name}`}>Change</button>
            </>}
        </div>
        {open ? <div className="grid gap-3 pl-8">{children}</div> : null}
    </section>;
}

export function ActivityGrantEditor({ onSaved, onCancel, enforcement = "unknown" }) {
    const [recipientKind, setRecipientKind] = useState("viewer");
    const [recipient, setRecipient] = useState(null);
    const [capability, setCapability] = useState(ACTIVITY_CAPABILITIES[0].value);
    const [groups, setGroups] = useState([]);
    const [expiryMode, setExpiryMode] = useState("never");
    const [expiry, setExpiry] = useState("");
    const [step, setStep] = useState("capability");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [uncertain, setUncertain] = useState(false);
    const operation = useRef(null);
    const live = useRef(false);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    const scopeChosen = groups.length > 0;
    const valid = Boolean(recipient && scopeChosen);
    const expiryTime = expiryMode === "date" ? new Date(expiry).getTime() : 0;
    const expiryChosen = expiryMode === "never" || (Number.isSafeInteger(expiryTime) && expiryTime > Date.now());
    const expirySummary = expiryMode === "never" ? "No expiry"
        : Number.isSafeInteger(expiryTime) ? new Date(expiryTime).toLocaleString() : "Choose a date";
    const preview = useScopeImpact({ recipient, recipientKind, groups, active: Boolean(recipient) && scopeChosen });
    const scopeSummary = !groups.length ? "Not chosen yet"
        : `${groups.map(groupName).join(", ")}${preview && !preview.degraded ? ` - ${preview.subjectCount} members` : ""}`;
    const save = async event => {
        event.preventDefault();
        if (busy || !valid) return;
        if (!operation.current) {
            const expiresAt = expiryMode === "date" ? new Date(expiry).getTime() : 0;
            if (expiryMode === "date" && (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now())) {
                setStep("expiry");
                setError("Choose an expiry in the future, or choose Never."); return;
            }
            operation.current = activityConfigurationMutation("createGrant", {
                recipient: recipientKind === "viewer" ? { viewerUserId: recipient.viewerUserId } : { directoryGroup: keyOnly(recipient) },
                grant: { capability, scope: { groups: { groups: groups.map(keyOnly) } }, expiresAtUnixMs: String(expiresAt) },
            });
        }
        setBusy(true); setError("");
        try { await operation.current(); if (live.current) onSaved(); }
        catch (failure) {
            if (!live.current) return;
            setUncertain(uncertainActivityMutation(failure));
            setError(activityConfigurationError(failure));
            if (!uncertainActivityMutation(failure)) operation.current = null;
        } finally { if (live.current) setBusy(false); }
    };
    return <form className={PANEL} onSubmit={save} aria-busy={busy} aria-label="New activity grant">
        <div className="flex items-baseline justify-between gap-3">
            <h3>New activity grant</h3>
            {enforcement === "off" ? <span className="shrink-0 rounded-full bg-[color:var(--green-soft)] px-2 py-[2px] text-[10px] uppercase tracking-wide text-[color:var(--muted)]">Enforcement off</span> : null}
        </div>
        {enforcement === "off" ? <p className="text-[11px] text-[color:var(--muted)]">This grant will be prepared without changing current access while enforcement is off.</p> : null}
        <fieldset disabled={busy || uncertain} className="grid gap-4">
            {!recipient ? <section data-grant-recipient-picker className="grid gap-3">
                <strong>Who should receive access?</strong>
                <p>Choose one user or group. The recipient stays fixed while you define what they can access.</p>
                <label className="grid gap-1">Recipient type
                    <select aria-label="Recipient type" className={INPUT} value={recipientKind} onChange={event => setRecipientKind(event.target.value)}>
                        <option value="viewer">Individual user</option><option value="group">Directory group members</option>
                    </select>
                </label>
                <CandidatePicker key={recipientKind} kind={recipientKind} title="Search recipients" selected={new Set()} onSelect={setRecipient} />
            </section> : <>
                <div className="flex flex-wrap items-baseline gap-2 border-l-2 border-[color:var(--green)] bg-[color:var(--green-soft)] p-3">
                    <strong>{recipientKind === "group" ? groupName(recipient) : recipient.displayName || "Individual user (name unavailable)"}</strong>
                    <span>{enforcement === "off" ? "will be able to" : enforcement === "on" ? "can" : "is configured to"}</span>
                </div>
                <div className="grid gap-3">
                    <GrantStep name="capability" position="1" answered value={labelFor(capability)} open={step === "capability"} onOpen={() => setStep("capability")}>
                        <label className="grid gap-1">Activity capability
                            <select aria-label="Activity capability" className={INPUT} value={capability} onChange={event => setCapability(event.target.value)}>
                                {ACTIVITY_CAPABILITIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                            </select>
                        </label>
                        <p className="text-[11px] text-[color:var(--muted)]">{ACTIVITY_CAPABILITIES.find(item => item.value === capability).description}</p>
                        <button type="button" className={`${BUTTON} justify-self-start`} onClick={() => setStep("scope")}>Continue</button>
                    </GrantStep>
                    <GrantStep name="scope" position="2" answered={scopeChosen} value={scopeSummary} open={step === "scope"} onOpen={() => setStep("scope")}>
                        <CandidatePicker kind="group" title="Search activity scope groups" selected={new Set(groups.map(groupKeyId))} onSelect={group => setGroups(previous => previous.some(item => groupKeyId(item) === groupKeyId(group)) ? previous.filter(item => groupKeyId(item) !== groupKeyId(group)) : previous.length < 32 ? [...previous, group] : previous)} />
                        {groups.length ? <ul className="flex flex-wrap gap-2">{groups.map(group => <li key={groupKeyId(group)} className="flex items-center gap-2 border border-[color:var(--line)] px-2 py-1">{groupName(group)}
                            <button type="button" className="cursor-pointer text-[color:var(--muted)]" aria-label={`Remove ${groupName(group)}`} onClick={() => setGroups(previous => previous.filter(item => groupKeyId(item) !== groupKeyId(group)))}>x</button></li>)}</ul> : null}
                        {groups.length === 32 ? <p className="text-[11px] text-[color:var(--muted)]">Up to 32 groups can be selected per grant.</p> : null}
                        {groups.length ? <p className="text-[11px] text-[color:var(--muted)]" aria-busy={!preview} role="status">{!preview ? "Checking directory scope..."
                            : preview.degraded ? "Directory scope could not be confirmed. Group access depends on current directory membership."
                                : `(${preview.subjectCount} members in scope)`}</p> : null}
                        <button type="button" className={`${BUTTON} justify-self-start`} disabled={!scopeChosen} onClick={() => setStep("expiry")}>Continue</button>
                    </GrantStep>
                    <GrantStep name="expiry" position="3" answered={expiryChosen} value={expirySummary} open={step === "expiry"} onOpen={() => setStep("expiry")}>
                        <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2"><input type="radio" name="grant-expiry" value="never" checked={expiryMode === "never"} onChange={() => { setExpiryMode("never"); setExpiry(""); setError(""); }} />Never</label>
                            <label className="flex items-center gap-2"><input type="radio" name="grant-expiry" value="date" checked={expiryMode === "date"} onChange={() => { setExpiryMode("date"); setError(""); }} />On a date</label>
                        </div>
                        {expiryMode === "date" ? <input className={`${INPUT} justify-self-start`} aria-label="Expires at (local time)" type="datetime-local" required value={expiry} onChange={event => { setExpiry(event.target.value); setError(""); }} /> : null}
                    </GrantStep>
                </div>
            </>}
        </fieldset>
        {error ? <p role="alert">{error}</p> : null}
        <div className="flex gap-2">
            <button type="submit" className={BUTTON} disabled={busy || !valid}>{busy ? "Saving grant..." : uncertain ? "Retry saving grant" : "Save activity grant"}</button>
            <button type="button" className={BUTTON} disabled={busy || uncertain} onClick={onCancel}>Cancel</button>
        </div>
    </form>;
}


function RevokeGrant({ row, onSaved }) {
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [uncertain, setUncertain] = useState(false);
    const operation = useRef(null);
    const live = useRef(false);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    const revoke = async () => {
        if (busy) return;
        operation.current ??= activityConfigurationMutation("revokeGrant", { grantId: row.id, expectedRevision: row.revision });
        setBusy(true); setError("");
        try { await operation.current(); if (live.current) onSaved(); }
        catch (failure) {
            if (!live.current) return;
            setError(activityConfigurationError(failure)); setUncertain(uncertainActivityMutation(failure));
            if (!uncertainActivityMutation(failure)) operation.current = null;
        } finally { if (live.current) setBusy(false); }
    };
    return <div aria-busy={busy}>
        {confirm ? <div className="flex gap-2">
            <button className={BUTTON_STRONG} disabled={busy || uncertain} onClick={() => { setConfirm(false); setError(""); }}>Cancel</button>
            <button className={BUTTON_QUIET} disabled={busy} onClick={revoke}>{busy ? "Revoking..." : uncertain ? "Retry" : "Revoke"}</button>
        </div> : <button className={BUTTON} onClick={() => setConfirm(true)}>Revoke grant</button>}
        {error ? <p role="alert">{error}</p> : null}
    </div>;
}

/// Collapses a page of grants under one heading per recipient, preserving the
/// order the server returned. Pagination is per grant, so one recipient's
/// grants can still land on separate pages.
function groupByRecipient(grants = []) {
    const order = [];
    const index = new Map();
    for (const row of grants) {
        const key = recipientKey(row.recipient);
        if (!index.has(key)) { index.set(key, order.length); order.push({ key, recipient: row.recipient, rows: [] }); }
        order[index.get(key)].rows.push(row);
    }
    return order;
}

function ActivityGrants() {
    const caps = useMyPermissions();
    const enforcement = activityEnforcement(caps);
    const canManage = hasSensitivePermission(caps, PERMISSIONS.GRANTS_MANAGE);
    const canChooseRecipients = hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const [afterId, setAfterId] = useState("");
    const [reload, setReload] = useState(0);
    const [result, setResult] = useState(null);
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState("");
    const query = JSON.stringify([afterId, reload]);
    const page = result?.query === query ? result : null;
    useEffect(() => {
        let current = true;
        listActivityGrants(afterId).then(value => { if (current) setResult({ ...value, query }); })
            .catch(() => { if (current) setResult({ query, error: true }); });
        return () => { current = false; };
    }, [afterId, query]);
    const saved = message => { setEditing(false); setMessage(message); setReload(value => value + 1); invalidateMyPermissions(); };
    return <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto p-4 text-[12px] text-[color:var(--ink)]">
        <header className="grid gap-2">
            <div className="flex flex-wrap items-baseline gap-2">
                <h2 className="text-[16px]">Activity access</h2>
                <EnforcementPill enforcement={enforcement} />
            </div>
            {enforcement === "off" ? <p>These grants are prepared for enforcement. Current access still follows users' roles.</p> : null}
            {enforcement === "unknown" ? <p role="alert">Current enforcement could not be confirmed. These grants may not reflect current access.</p> : null}
            {canManage && canChooseRecipients && !editing ? <button className={`${BUTTON} justify-self-start`} onClick={() => { setEditing(true); setMessage(""); }}>New activity grant</button> : null}
            {canManage && !canChooseRecipients ? <p>Choosing recipients requires directory browsing permission.</p> : null}
        </header>
        {message ? <p role="status">{message}</p> : null}
        <OverlayDialog open={editing && canManage && canChooseRecipients} onClose={() => setEditing(false)}
            analyticsId={DIALOG.ACTIVITY_GRANT} eyebrow="Activity access" title="Grant access" width="560px" maxWidth="96vw" dismissible={false}>
            {editing ? <ActivityGrantEditor enforcement={enforcement} onCancel={() => setEditing(false)} onSaved={() => saved(enforcement === "off"
                ? "Activity grant prepared. Current access is unchanged while enforcement is off."
                : enforcement === "on" ? "Activity grant saved." : "Activity grant saved. Enforcement status could not be confirmed.")} /> : null}
        </OverlayDialog>
        <section className={PANEL} aria-label="Configured activity grants" aria-busy={!page}>
            {!page ? <p>Loading grants...</p> : page.error ? <p role="alert">Activity grants could not be loaded.</p> : <>
                {!page.grants?.length ? <p>No activity grants on this page.</p> : null}
                {groupByRecipient(page.grants).map(entry => <section key={entry.key} data-grant-recipient
                    className="overflow-hidden rounded-[2px] border border-[color:var(--line)]">
                    <div className="flex flex-wrap items-baseline gap-2 border-b border-[color:var(--line)] bg-[color:var(--control-bg)] px-3 py-2">
                        <h3 className="font-medium">{recipientName(entry.recipient)}</h3>
                        <span className="text-[11px] text-[color:var(--muted)]">{entry.rows.length === 1 ? "1 grant" : `${entry.rows.length} grants`}</span>
                    </div>
                    {entry.rows.map((row, position) => {
                        const revoked = Number(row.revokedAtUnixMs) > 0;
                        const expiry = Number(row.grant?.expiresAtUnixMs);
                        const expired = expiry > 0 && expiry <= Date.now();
                        const ended = revoked || expired;
                        const directoryInactive = row.inactiveNotPrimaryDirectory === true && !ended;
                        const raw = row.grant?.capability === RAW_PROMPT_CAPABILITY;
                        return <div key={`${row.id}:${row.revision}`} data-activity-grant-id={row.id}
                            className={`flex flex-wrap items-center gap-2 px-3 py-2 sm:grid sm:grid-cols-[9.5rem_minmax(0,1fr)_auto_auto] sm:gap-3 ${position ? "border-t border-[color:var(--line-soft)]" : ""} ${ended ? "opacity-60" : ""}`}>
                            <span title={labelFor(row.grant?.capability)}
                                className={`justify-self-start rounded-[2px] border px-2 py-[2px] text-[11px] ${raw ? "border-[color:var(--ink)] font-medium text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--muted)]"}`}>{shortLabelFor(row.grant?.capability)}</span>
                            <span className="min-w-0 truncate" title={compactScope(row.grant?.scope, 32)}>{compactScope(row.grant?.scope)}</span>
                            <span className="text-[11px] text-[color:var(--muted)]">
                                {ended ? <span className="rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] uppercase tracking-wide">{revoked ? "Revoked" : "Expired"}</span>
                                    : directoryInactive ? <span className="rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[color:var(--ink)]">Inactive: not primary directory</span>
                                    : expiry > 0 ? `until ${new Date(expiry).toLocaleDateString()}` : null}
                            </span>
                            <span className="justify-self-end">
                                {canManage && !ended ? <RevokeGrant row={row} onSaved={() => saved(enforcement === "off"
                                    ? "Prepared activity grant revoked. Current role-based access is unchanged while enforcement is off."
                                    : enforcement === "on" ? "Activity grant revoked. Other activity grants may still provide access."
                                        : "Activity grant revoked. Enforcement status could not be confirmed.")} /> : null}
                            </span>
                            {directoryInactive ? <p className="w-full text-[11px] text-[color:var(--muted)] sm:col-span-4">This grant gives no access. Changing the primary directory may reactivate it; revoke it to prevent future access.</p> : null}
                        </div>;
                    })}
                </section>)}
            </>}
            <div className="flex gap-2">
                <button className={BUTTON} disabled={!page} onClick={() => setReload(value => value + 1)}>Refresh grants</button>
                {afterId ? <button className={BUTTON} disabled={!page} onClick={() => setAfterId("")}>First page</button> : null}
                {page?.nextAfterId ? <button className={BUTTON} onClick={() => setAfterId(page.nextAfterId)}>Next page</button> : null}
            </div>
        </section>
    </div>;
}
